import React, { useState, useRef } from 'react';

import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { useEntidad, useLista, useManagedContext, useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { ServerRequest } from '../../utils/apiweb';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { PERMISSIONS } from '../../consts/permissions';
import { usePermissions } from '../../components/hooks/usePermissions';
import {TIPS} from "../../consts/tips";


function TasasView() {

    //hooks
    const { showMessageModal } = useManagedContext()

    const [state, setState] = useState({
        loading: false,
        showForm: false,
        rowId: 0,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const searchRef = useRef();
    const tableRef = useRef();

    const [tableVisible, setTableVisible] = useState(false);
    
    const [, getRowLista ] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (listas, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoTributo',
          timeout: 0
        }
    });

    const [, getRowEntidad] = useEntidad({
        entidades: ['CategoriaTasa'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'CategoriaTasa',
          timeout: 0
        }
      });

    const getDescTipoTributo = (id) => {
        const row = getRowLista('TipoTributo', id);
        return (row) ? row.nombre : '';
    }    

    const getDescCategoriaTasa = (id) => {
        const row = getRowEntidad('CategoriaTasa', id);
        return (row) ? row.nombre : '';
    }    

    //definiciones
    const tableColumns = [
        { Header: 'Código', id: 'codigo', accessor: 'codigo', width: '10%' },        
        { Header: 'Descripción', accessor: 'descripcion', width: '40%' },
        { Header: 'Tipo de tributo', Cell: (props) => {
            const row = props.row.original;
            const tipoTributo = getDescTipoTributo(row.idTipoTributo);
            return tipoTributo;
          }, accessor: 'tipoTributo', width: '20%' },
        { Header: 'Categoría', Cell: (props) => {
            const row = props.row.original;
            const categoriaTasa = getDescCategoriaTasa(row.idCategoriaTasa);
            return categoriaTasa;
          }, accessor: 'idCategoriaTasa', width: '20%' },
    ]; 

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.TASA_EDIT);

    //handles
    const avmr = {
        onAdd: () => {
            navigateWithFilters(`/tasa/${OPERATION_MODE.NEW}`);
        },
        onView: ({ id }) => {
            navigateWithFilters(`/tasa/${OPERATION_MODE.VIEW}/${id}`);
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/tasa/${OPERATION_MODE.EDIT}/${id}`);
        },
        onRemove: ({ id }) => {
            const tasa = state.list.find(x => x.id === id)
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la tasa ${tasa.codigo}?`,
                onConfirm: () => {
                    RemoveTasa(id)
                }
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: {showModal: true, idEntidad: id, disabled}}))
        },
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }  

    const SearchTasasWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.TASA,
        buildParamsUrl: (filters) => {
            return `/filter?` +
                `codigo=${encodeURIComponent(filters.codigo || '')}&` +
                `descripcion=${encodeURIComponent(filters.descripcion || '')}&` +
                `etiqueta=${encodeURIComponent(filters.etiqueta || '')}`;
        },
        callbackNoSuccess,
        callbackError
    });

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchTasasWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //funciones
    function SearchTasas() {
        SearchTasasWithSavedFilters(getCurrentFilters());
    }

    function RemoveTasa(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchTasas();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.TASA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );        
    }


    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `la tasa ${dataTaggerRow.descripcion}` : ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de la tasa"
                registro={dataTaggerRegistro}
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_TASA_MODAL}
                entidad="Tasa"
                idEntidad={state.dataTagger.idEntidad}
                disabled={state.dataTagger.disabled}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        <SectionHeading titles={[{ title: 'Entidades' }, { title: 'Tasas' }]}
                        onAddClick={canEdit ? avmr.onAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>
            
            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'Código', field: 'codigo', type: 'string', size: 1, searchOnEnter: true },
                    { title: 'Descripción', field: 'descripcion', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 3, searchOnEnter: true }
                ]}
                onSearch={SearchTasas}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode={PERMISSIONS.TASA_EDIT}
                    restoredPageIndex={restoredPageIndex}
                    defaultGlobalFilter={restoredGlobalFilter}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default TasasView;

